import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from '@/store';
import App from './App';
import '@/i18n/config';
import '@/styles/global.css';
import { devAutoLogin } from '@/utils/devAuth';

/**
 * ==============================================================
 * MSW MOCK API SETUP
 * ==============================================================
 *
 * PERMANENT under the two-artifact model (docs/DEMO-DEPLOYMENT.md):
 * the mocks power the dev server (`dev:mock*`) AND the deployable
 * DEMO artifact (the default `npm run build`). Real-API artifacts
 * (`build:api` / `build:uat` / `build:dev`) dead-code-eliminate this
 * block via the inline env gate below. Do NOT delete the /mocks
 * directory or this block — that would kill the demo artifact.
 * ==============================================================
 */

// ============== START: MSW SETUP ==============
/**
 * Unregister any existing MSW service workers
 * This ensures clean switch between mock and real API modes
 */
async function unregisterMSWServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      // Check if this is an MSW service worker
      if (registration.active?.scriptURL?.includes('mockServiceWorker')) {
        console.log('[MSW] Unregistering cached MSW service worker...');
        await registration.unregister();
        console.log('[MSW] Service worker unregistered. Reloading for clean state...');
        // Reload to ensure clean state without MSW intercepting requests
        window.location.reload();
        return true; // Indicate reload is happening
      }
    }
  }
  return false;
}

/**
 * Whether this build/run should mock the API with MSW.
 *
 * Gated on the VITE_USE_MSW FLAG (+ a positive non-production allowlist), NOT on
 * `import.meta.env.DEV`. Vite forces `import.meta.env.DEV=false` for ANY
 * `vite build` (even `--mode development`) because DEV/PROD follow NODE_ENV, not
 * --mode — so a DEV-gated boot tree-shakes the mocks out of every deployable
 * build. Gating on the flag lets a regular `--mode development` build ship a
 * self-contained mock demo (see `build:demo`), while `--mode production` can
 * never mock. Read `import.meta.env.*` inline here so Vite statically inlines
 * the values and Rollup can DCE the mock chunk when this is false.
 */
const USE_MSW = import.meta.env.VITE_USE_MSW === 'true' && import.meta.env.MODE !== 'production';

async function enableMocking() {
  console.log(
    '[MSW Check] MODE:',
    import.meta.env.MODE,
    'VITE_USE_MSW:',
    import.meta.env.VITE_USE_MSW,
    '=> mocking:',
    USE_MSW
  );

  if (!USE_MSW) {
    // Mocking is off in this build/run. If a stale MSW service worker is still
    // registered from a prior mock-mode visit (service workers persist across
    // visits), tear it down once so the app hits the real network — the runtime
    // twin of the production kill-switch in vite.config.ts.
    const reloading = await unregisterMSWServiceWorker();
    if (reloading) return; // Stop execution, page will reload
    return;
  }

  // MIXED-SEAM GUARD: MSW is on, yet some modules are flipped to the live
  // GraphQL seam (VITE_LIVE_MODULES — usually a leftover .env.*.local from
  // live-seam work). Their traffic will BYPASS the mocks and die if the :8090
  // server is down — the exact "mocks are broken" symptom of 2026-08-20
  // (login + all masters pages). The dev:mock* scripts pin the variable empty;
  // this banner catches any other boot path. Loud, not fatal: mixed mode is
  // legitimate when the live server is actually running.
  const { liveModules } = await import('@/services/api/client');
  if (liveModules.size > 0) {
    console.error(
      `[SEAM] MIXED MODE: MSW is ON but these modules ride the LIVE server and ` +
        `bypass every mock: ${[...liveModules].join(', ')}. If login or these ` +
        `surfaces fail, the live server is down — unset VITE_LIVE_MODULES ` +
        `(check .env.development.local) or use a dev:mock* script, which pins it empty.`
    );
  }

  const { startMockServiceWorker } = await import('../mocks/setup');
  return startMockServiceWorker();
}
// ============== END: MSW SETUP ==============

// Start the app (keep this!)
enableMocking().then(async () => {
  // Auto-login only in the real-API dev server (not in mock mode — the MSW auth
  // handler owns the session there).
  if (!USE_MSW && import.meta.env.DEV) {
    await devAutoLogin();
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>
  );
});
